import SellerMarketplaceCard from './components/SellerOfferCard';

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate } from 'react-router-dom';
import { getImage } from 'variables/images';
import { Industry, productKey, useIndustries } from 'services/industryService';
import WelcomeSellerModal from '../dashboard/components/WelcomeSellerModal';
import SignUpSellerModal from '../dashboard/components/SignUpSellerModal';
import KYCBanner from 'components/KYCBanner';
import { MdAdd, MdCheck, MdExpandMore, MdOutlineShoppingBasket, MdTrendingUp } from 'react-icons/md';

// Catch-all "Autre(s)" sectors / products ("Autre", "Autres", "Autre projet"...) aren't
// something a seller targets: never shown on the marketplace.
const isCatchAll = (name: string) => /^autres?(-|$)/.test(productKey(name));

// Same rule as the back (services/demandCountries.ts): only explicit foreign areas
// aren't France.
const COUNTRIES: Record<string, { label: string; flag: string; area?: RegExp }> = {
    FR: { label: 'France', flag: 'linear-gradient(90deg,#0055A4 33.3%,#FFFFFF 33.3% 66.6%,#EF4135 66.6%)' },
    BE: { label: 'Belgique', flag: 'linear-gradient(90deg,#1A1613 33.3%,#FDDA24 33.3% 66.6%,#EF3340 66.6%)', area: /belgique/i },
    CH: {
        label: 'Suisse',
        flag: 'linear-gradient(#FFFFFF,#FFFFFF) center/54% 22% no-repeat, linear-gradient(#FFFFFF,#FFFFFF) center/16% 62% no-repeat, #D52B1E',
        area: /suisse/i,
    },
    ES: { label: 'Espagne', flag: 'linear-gradient(180deg,#AA151B 25%,#F1BF00 25% 75%,#AA151B 75%)', area: /espagne/i },
};
const areaCountry = (area: string) =>
    Object.keys(COUNTRIES).find((cc) => COUNTRIES[cc].area?.test(area)) ?? 'FR';

// Cards shown per industry before "Voir les N thématiques" in the "Toutes" tab
const PREVIEW = 6;

interface Theme {
    key: string;
    name: string;
    country: string;
    leads: number;
    hasDemand: boolean;
    minPrice: number | null;
    departments: string[];
}

interface ThemeGroup {
    id: string;
    label: string;
    items: Theme[];
    themes: number;
    leads: number;
}

const Pill = ({ children, className }: { children: React.ReactNode; className: string }) => (
    <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-semibold ${className}`}>
        {children}
    </span>
);

const ThemeCard = ({ t, onClick }: { t: Theme; onClick: () => void }) => {
    const hasVolume = t.leads > 0;
    return (
        <button
            onClick={onClick}
            title={t.departments.length ? `Départements : ${t.departments.join(', ')}` : undefined}
            className={`flex min-w-0 flex-col gap-1.5 rounded-[10px] border bg-white px-[11px] py-[9px] text-left transition-colors hover:border-[#1E9E52] ${
                hasVolume ? 'border-[#B6E0C4]' : 'border-[#DDEFE4]'
            }`}
        >
            <span className="flex min-w-0 items-start gap-[7px]">
                <span
                    title={COUNTRIES[t.country]?.label}
                    className="mt-0.5 h-3 w-[18px] shrink-0 rounded-[2px] shadow-[0_0_0_1px_rgba(20,17,14,0.12)]"
                    style={{ background: COUNTRIES[t.country]?.flag }}
                />
                <span
                    className={`min-w-0 flex-1 text-[12.5px] leading-[1.3] text-[#1A1613] ${hasVolume ? 'font-semibold' : 'font-normal'}`}
                >
                    {t.name}
                </span>
            </span>
            <span className="flex items-center justify-between gap-1.5 whitespace-nowrap pl-[25px] text-[11.5px] tabular-nums">
                {hasVolume ? (
                    <span className="inline-flex items-center gap-[5px] font-bold text-[#146334]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#1E9E52]" />
                        {t.leads.toLocaleString('fr-FR')} leads
                    </span>
                ) : (
                    <span />
                )}
                {t.minPrice != null && <span className="font-semibold text-[#3B352C]">dès {t.minPrice.toFixed(2)} €</span>}
            </span>
        </button>
    );
};

const BuyerOffer = () => {
    const HERO_STORAGE_KEY = 'sellerMarketplaceHeroHidden';
    const [offers, setOffers] = useState([]);
    const [user, setUser] = useState(null);
    const [mySellerOffers, setMySellerOffers] = useState<any[]>([]);
    const [templatesCount, setTemplatesCount] = useState(0);
    const [showHeroCard, setShowHeroCard] = useState(true);
    const [selectedSubIndustry, setSelectedSubIndustry] = useState('all');
    const [selectedPriceRange, setSelectedPriceRange] = useState('all');
    const [selectedLeadRange, setSelectedLeadRange] = useState('all');
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [purchaseOrderVolumes, setPurchaseOrderVolumes] = useState([]);
    const [selectedIndustry, setSelectedIndustry] = useState('all');
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    const { industries, loading: industriesLoading } = useIndustries();
    const nav = useNavigate();

    const isGuest = user?.email?.includes('guest-') ?? false;

    const priceRanges = [
        { value: 'all', label: 'Tous' },
        { value: '0-10', label: '0 - 10€' },
        { value: '10-20', label: '10 - 20€' },
        { value: '20-50', label: '20 - 50€' },
        { value: '50+', label: '50€ +' },
    ];

    const leadRanges = [
        { value: 'all', label: 'Tous' },
        { value: '0-50', label: '0 - 50' },
        { value: '50-200', label: '50 - 200' },
        { value: '200-500', label: '200 - 500' },
        { value: '500+', label: '500 +' },
    ];

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                const user = res.data;
                if (user.kyc_url) {
                    setTimeout(() => { window.location.href = user.kyc_url; }, 3000);
                }
                setUser(res.data);
                if (res.data && res.data.type !== 'seller') {
                    window.location.href = '/dashboard';
                }
            })
            .catch((err) => console.log('No user info found', err));
    };

    const initOffer = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/offer`, config)
            .then((res) => setOffers(res.data))
            .catch((err) => console.log('No user info found', err));
    };

    const initPurchaseOrderVolumes = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/purchase-order/volumes`, config)
            .then((res) => setPurchaseOrderVolumes(res.data))
            .catch((err) => console.log('Error fetching purchase order volumes', err));
    };

    const initSellerSetupProgress = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };

        axios
            .all([
                axios.get(`${CONFIG.apiUrl}/seller-offer/mine`, config),
                axios.get(`${CONFIG.apiUrl}/form-template`, config),
            ])
            .then(
                axios.spread((sellerOffersRes, templatesRes) => {
                    setMySellerOffers(sellerOffersRes.data || []);
                    setTemplatesCount((templatesRes.data || []).length);
                })
            )
            .catch((err) => console.log('Error fetching seller setup progress', err));
    };

    useEffect(() => {
        const heroHidden = localStorage.getItem(HERO_STORAGE_KEY) === 'true';
        if (heroHidden) {
            setShowHeroCard(false);
        }

        if (!localStorage.getItem('token') || localStorage.getItem('token').length < 10)
            window.location.href = '/auth/sign-up';
        initOffer();
        initUser();
        initPurchaseOrderVolumes();
        initSellerSetupProgress();
    }, []);

    const hideHeroCard = () => {
        localStorage.setItem(HERO_STORAGE_KEY, 'true');
        setShowHeroCard(false);
    };

    const marketOffers = offers.filter((o) => o.status === 'on_market');

    const subIndustries = useMemo(() => {
        const unique = [...new Set(marketOffers.map((o) => o.subIndustry).filter(Boolean))];
        return unique.sort();
    }, [marketOffers]);

    const sellerCommissionRate: number =
        (offers as any[]).find((o: any) => o.commissionRate != null)?.commissionRate ?? 0.15;
    const sellerCommissionFixedPrice: number | null =
        (offers as any[]).find((o: any) => o.commissionFixedPrice != null)?.commissionFixedPrice ?? null;

    const volumeToSellerPrice = (buyerPrice: number) => {
        if (sellerCommissionFixedPrice != null) return buyerPrice - sellerCommissionFixedPrice;
        return buyerPrice / (1 + sellerCommissionRate);
    };

    const toSellerPrice = (buyerPrice: number, offer: any) => {
        const fixedMargin = Number(offer.fixedMargin) || 0;
        if (offer.commissionFixedPrice != null) return buyerPrice - offer.commissionFixedPrice - fixedMargin;
        const commissionRate = offer.commissionRate ?? sellerCommissionRate;
        return (buyerPrice - fixedMargin) / (1 + commissionRate);
    };

    const getMinRealPrice = (offer: any) => {
        const values = offer.targetPrice.replace('€', '').split('-');
        return toSellerPrice(Number(values[0]), offer);
    };

    const filteredOffers = marketOffers.filter((o) => {
        if (selectedSubIndustry !== 'all' && o.subIndustry !== selectedSubIndustry) return false;
        if (selectedPriceRange !== 'all') {
            const minPrice = getMinRealPrice(o);
            if (selectedPriceRange === '50+') { if (minPrice < 50) return false; }
            else {
                const [low, high] = selectedPriceRange.split('-').map(Number);
                if (minPrice < low || minPrice >= high) return false;
            }
        }
        if (selectedLeadRange !== 'all') {
            const qty = o.weeklyQuantity || 0;
            if (selectedLeadRange === '500+') { if (qty < 500) return false; }
            else {
                const [low, high] = selectedLeadRange.split('-').map(Number);
                if (qty < low || qty >= high) return false;
            }
        }
        return true;
    });

    // Shuffle once per fetch, not on every render (filters, accordion toggles...)
    const shuffleRank = useMemo(() => {
        const rank = new Map<string, number>();
        (offers as any[]).forEach((o) => rank.set(o._id, Math.random()));
        return rank;
    }, [offers]);

    const visibleOffers = filteredOffers
        .filter((offer) => getMinRealPrice(offer) >= 10)
        .sort((a, b) => (shuffleRank.get(a._id) ?? 0) - (shuffleRank.get(b._id) ?? 0));

    const displayRealPrice = (price: string, offer: any) => {
        const values = price.replace('€', '').split('-');
        const minSeller = toSellerPrice(Number(values[0]), offer);
        const maxSeller = toSellerPrice(Number(values[1]), offer);
        return `${minSeller.toFixed(2)} – ${maxSeller.toFixed(2)}`;
    };

    // Purchase-order demand laid over the catalog: every sub-industry is a "thématique",
    // with its open volume when buyers are looking for it.
    const themeGroups: ThemeGroup[] = useMemo(() => {
        const volumeByKey = new Map<string, any>();
        for (const vol of purchaseOrderVolumes as any[]) volumeByKey.set(productKey(vol.subIndustry), vol);

        const sellerMin = (buyerPrice: number | null | undefined) => {
            if (buyerPrice == null || !buyerPrice) return null;
            const p = volumeToSellerPrice(buyerPrice);
            return p > 0 ? p : null;
        };

        // One card per country: the back-office list when set, otherwise the countries the
        // demand covers (France when there is none). Price: back-office "à partir de" (already
        // seller-side), else the cheapest open order for that country, else the catalog.
        // Volume: back-office value for that country, else the open demand.
        const toThemes = (sub: Industry['subIndustries'][number], vol: any): Theme[] => {
            const settings = sub.marketplace;
            const demand: any[] = !vol
                ? []
                : vol.countries?.length
                ? vol.countries
                : [{ country: 'FR', leads: vol.totalLeads, minPricePerLead: vol.minPricePerLead }];
            const countries = settings?.countries?.length
                ? settings.countries
                : demand.length
                ? demand.map((c) => c.country)
                : ['FR'];
            const displayPrice = settings?.displayPrice != null && settings.displayPrice > 0 ? settings.displayPrice : null;
            return countries.map((country) => {
                const c = demand.find((d) => d.country === country);
                return {
                    key: `${productKey(sub.name)}-${country}`,
                    name: sub.name,
                    country,
                    leads: settings?.volumes?.[country] ?? c?.leads ?? 0,
                    hasDemand: !!c,
                    minPrice: displayPrice ?? (c ? sellerMin(c.minPricePerLead) : sellerMin(sub.priceMin)),
                    departments: (vol?.departments ?? []).filter((d: string) => areaCountry(d) === country),
                };
            });
        };

        // A product's total: the open demand counts an order covering several countries
        // once (it shows on each card), unless the back-office set volumes by hand.
        const productLeads = (sub: Industry['subIndustries'][number], vol: any, items: Theme[]) =>
            Object.keys(sub.marketplace?.volumes ?? {}).length
                ? items.filter((t) => t.name === sub.name).reduce((n, t) => n + t.leads, 0)
                : vol?.totalLeads ?? 0;

        const byDemand = (a: Theme, b: Theme) =>
            b.leads - a.leads || Number(b.hasDemand) - Number(a.hasDemand) || a.name.localeCompare(b.name, 'fr');

        const sellable = industries.filter((industry) => !isCatchAll(industry.name));
        const groups: ThemeGroup[] = sellable.map((industry) => {
            const subs = industry.subIndustries.filter((sub) => !sub.marketplace?.hidden && !isCatchAll(sub.name));
            const volumes = subs.map((sub) => volumeByKey.get(productKey(sub.name)));
            const items = subs.flatMap((sub, i) => toThemes(sub, volumes[i]));
            items.sort(byDemand);
            return {
                id: productKey(industry.name),
                label: industry.name,
                items,
                themes: subs.length,
                leads: subs.reduce((n, sub, i) => n + productLeads(sub, volumes[i], items), 0),
            };
        });

        return groups.filter((g) => g.items.length > 0).sort((a, b) => b.leads - a.leads);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industries, purchaseOrderVolumes, sellerCommissionRate, sellerCommissionFixedPrice]);

    const themeTotal = themeGroups.reduce((n, g) => n + g.themes, 0);
    const themeLeads = themeGroups.reduce((n, g) => n + g.leads, 0);
    const shownGroups = themeGroups.filter((g) => selectedIndustry === 'all' || g.id === selectedIndustry);
    const multiGroups = shownGroups.length > 1;
    const allGroupsOpen = themeGroups.length > 0 && themeGroups.every((g) => openGroups[g.id]);
    const toggleAllGroups = () => {
        const next: Record<string, boolean> = {};
        themeGroups.forEach((g) => { next[g.id] = !allGroupsOpen; });
        setOpenGroups(next);
    };

    const isSameUserId = (id: any) => String(id) === String(user?._id);
    const hasCreatedOffer = mySellerOffers.some((offer: any) => isSameUserId(offer?.sellerId));
    const hasJoinedExistingOffer = mySellerOffers.some(
        (offer: any) => isSameUserId(offer?.sellerId) && offer?.status && offer?.status !== 'on_market'
    );
    const hasCreatedIframe = templatesCount > 0;

    const setupActions = [
        {
            key: 'create-iframe',
            title: 'Brancher mon site',
            description: 'Pour commencer à vendre vos leads.',
            done: hasCreatedIframe,
            onClick: () => nav('/integrations'),
        },
        {
            key: 'join-offer',
            title: 'Répondre à une demande',
            description: "Vendez sur un ordre d'achat ouvert.",
            done: hasJoinedExistingOffer,
            onClick: () => document.getElementById('marketplace-demand-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        },
        {
            key: 'create-offer',
            title: 'Publier une offre',
            description: 'Votre offre au prix fixe sur le marché.',
            done: hasCreatedOffer,
            onClick: () => nav('/seller-offer'),
        },
    ];
    const setupDone = setupActions.filter((a) => a.done).length;

    return (
        <div className="mx-auto flex max-w-[1140px] flex-col gap-7 pb-16 font-hanken text-[#1A1613]">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-6 rounded-[20px] border border-[#EDE7DE] bg-white px-8 py-[30px]">
                <div className="min-w-0">
                    <div className="mb-2 text-[11.5px] font-bold uppercase tracking-[0.11em] text-[#A79E90]">Vendeur</div>
                    <h1 className="mb-2.5 font-newsreader text-[38px] font-semibold leading-tight tracking-[-0.02em]">
                        Marketplace
                    </h1>
                    <p className="text-[15px] leading-relaxed text-[#6B6357]">
                        Deux façons de vendre : répondre à une demande d&apos;acheteur, ou publier une offre au prix fixe.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2.5">
                    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-[11px] border border-[#C2E5CE] bg-[#E7F6EC] px-3.5 py-2.5 text-[13.5px] font-semibold text-[#146334]">
                        <span className="h-[7px] w-[7px] rounded-full bg-[#1E9E52]" />
                        {themeLeads.toLocaleString('fr-FR')} leads recherchés
                    </span>
                    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-[11px] border border-[#EFE0AE] bg-[#FCF3D6] px-3.5 py-2.5 text-[13.5px] font-semibold text-[#6E5205]">
                        <span className="h-[7px] w-[7px] rounded-full bg-[#D9A100]" />
                        {marketOffers.length} commandes en vente
                    </span>
                </div>
            </div>

            {/* KYC Banner */}
            <KYCBanner user={user} onUserRefresh={initUser} />

            {/* Setup progress */}
            {showHeroCard && (
                <div className="rounded-[20px] border border-[#EDE7DE] bg-white p-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-3 pb-2 pt-2">
                        <span className="flex items-center gap-3">
                            <span className="text-[12px] font-bold uppercase tracking-[0.09em] text-[#A79E90]">Mise en route</span>
                            <span className="text-[12.5px] font-semibold tabular-nums text-[#146334]">{setupDone}/3</span>
                        </span>
                        <button
                            onClick={hideHeroCard}
                            className="rounded-[9px] border border-[#DDD5C9] bg-white px-3 py-1.5 text-[12.5px] font-semibold text-[#3B352C] transition-colors hover:bg-[#FAF8F4]"
                        >
                            Masquer
                        </button>
                    </div>
                    <div className="grid grid-cols-1 gap-0.5 md:grid-cols-3">
                        {setupActions.map((action, index) => (
                            <button
                                key={action.key}
                                onClick={action.onClick}
                                className="flex w-full items-start gap-3.5 rounded-[13px] px-[13px] py-[14px] text-left transition-colors hover:bg-[#FAF3E8]"
                            >
                                <span
                                    className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[12.5px] font-bold ${
                                        action.done ? 'bg-[#D9F0DE] text-[#146334]' : 'bg-[#241C5C] text-white'
                                    }`}
                                >
                                    {action.done ? <MdCheck className="h-4 w-4" /> : index + 1}
                                </span>
                                <span className="flex min-w-0 flex-col gap-0.5">
                                    <span className="text-[14.5px] font-semibold text-[#14110E]">{action.title}</span>
                                    <span className="text-[12.5px] leading-snug text-[#8A8172]">{action.description}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Purchase orders — the demand */}
            <div id="marketplace-demand-section" className="rounded-[22px] border border-[#C2E5CE] bg-[#F1F9F4] px-7 py-[26px]">
                <div className="mb-[22px] flex flex-wrap items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-[#1E9E52]">
                        <MdTrendingUp className="h-[22px] w-[22px] text-white" />
                    </span>
                    <div className="min-w-0 flex-[1_1_320px]">
                        <div className="mb-1 flex flex-wrap items-center gap-2.5">
                            <h2 className="font-newsreader text-[27px] font-semibold tracking-[-0.01em] text-[#0F4F2A]">
                                Ordres d&apos;achat
                            </h2>
                            <Pill className="bg-[#1E9E52] text-white">LA DEMANDE</Pill>
                        </div>
                        <p className="text-[14.5px] leading-relaxed text-[#3E6B4F]">
                            Des acheteurs recherchent <strong>{themeLeads.toLocaleString('fr-FR')} leads</strong> dès
                            maintenant, sur {themeTotal} thématiques ouvertes à la vente. Vous êtes payé au prix affiché,
                            sans attendre d&apos;acheteur.
                        </p>
                    </div>
                </div>

                {themeGroups.length === 0 ? (
                    <div className="rounded-[13px] border border-[#C2E5CE] bg-white px-4 py-8 text-center text-[14px] text-[#3E6B4F]">
                        {industriesLoading ? 'Chargement des thématiques…' : 'Aucune demande ouverte pour le moment.'}
                    </div>
                ) : (
                    <>
                        <div className="mb-[18px] flex flex-wrap items-center gap-[7px]">
                            {[{ id: 'all', label: 'Toutes', leads: themeLeads }, ...themeGroups].map((g) => {
                                const on = selectedIndustry === g.id;
                                return (
                                    <button
                                        key={g.id}
                                        onClick={() => setSelectedIndustry(g.id)}
                                        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                                            on
                                                ? 'border-[#146334] bg-[#146334] text-white'
                                                : 'border-[#C2E5CE] bg-white text-[#0F4F2A] hover:border-[#146334]'
                                        }`}
                                    >
                                        {g.label}
                                        <span className={`text-[11px] font-bold tabular-nums ${on ? 'text-[#BFE8CD]' : 'text-[#1E9E52]'}`}>
                                            {g.leads.toLocaleString('fr-FR')}
                                        </span>
                                    </button>
                                );
                            })}
                            <span className="flex-1" />
                            <span className="flex flex-wrap gap-3.5 text-[12px] text-[#3E6B4F]">
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#1E9E52]" />
                                    Volume recherché
                                </span>
                                {selectedIndustry === 'all' && (
                                    <button
                                        onClick={toggleAllGroups}
                                        className="font-bold text-[#146334] underline underline-offset-[3px]"
                                    >
                                        {allGroupsOpen ? 'Tout replier' : 'Tout déplier'}
                                    </button>
                                )}
                            </span>
                        </div>

                        <div className="flex flex-col gap-[22px]">
                            {shownGroups.map((g) => {
                                const isOpen = !multiGroups || !!openGroups[g.id];
                                const items = isOpen ? g.items : g.items.slice(0, PREVIEW);
                                return (
                                    <div key={g.id}>
                                        <div className="mb-2.5 flex flex-wrap items-center gap-2.5">
                                            <h3 className="font-newsreader text-[20px] font-semibold tracking-[-0.01em] text-[#0F4F2A]">
                                                {g.label}
                                            </h3>
                                            <span className="min-w-0 flex-1 text-[12.5px] tabular-nums text-[#3E6B4F]">
                                                {g.themes} thématiques
                                                {g.leads ? ` · ${g.leads.toLocaleString('fr-FR')} leads recherchés` : ''}
                                            </span>
                                            {multiGroups && g.items.length > PREVIEW && (
                                                <button
                                                    onClick={() => setOpenGroups({ ...openGroups, [g.id]: !openGroups[g.id] })}
                                                    className="inline-flex shrink-0 items-center gap-[7px] rounded-full border border-[#C2E5CE] bg-white px-[13px] py-[7px] text-[12.5px] font-semibold text-[#146334] transition-colors hover:border-[#146334]"
                                                >
                                                    {isOpen ? 'Replier' : `Voir tout (${g.items.length})`}
                                                    <MdExpandMore
                                                        className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                                    />
                                                </button>
                                            )}
                                        </div>
                                        <div
                                            className="grid gap-[7px]"
                                            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(182px, 1fr))' }}
                                        >
                                            {items.map((t) => (
                                                <ThemeCard key={t.key} t={t} onClick={() => nav('/integrations')} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Fixed-price offers on the market */}
            <div id="marketplace-offers-section" className="rounded-[22px] border border-[#EFE0AE] bg-[#FDF8EA] px-7 py-[26px]">
                <div className="mb-5 flex flex-wrap items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-[#F9DC5C]">
                        <MdOutlineShoppingBasket className="h-[22px] w-[22px] text-[#5B4405]" />
                    </span>
                    <div className="min-w-0 flex-[1_1_300px]">
                        <div className="mb-1 flex flex-wrap items-center gap-2.5">
                            <h2 className="font-newsreader text-[27px] font-semibold tracking-[-0.01em] text-[#4A3703]">
                                Commandes
                            </h2>
                            <Pill className="bg-[#F9DC5C] text-[#4A3703]">AU PRIX FIXE</Pill>
                        </div>
                        <p className="text-[14.5px] leading-relaxed text-[#6E5A22]">
                            Les offres fixes publiées sur le marché. Elles restent visibles et se vendent au fil des demandes.
                        </p>
                    </div>
                    <button
                        onClick={() => nav('/seller-offer')}
                        className="inline-flex shrink-0 items-center gap-2.5 rounded-[11px] bg-[#F9DC5C] px-[18px] py-3 text-[14.5px] font-semibold text-[#14110E] transition-colors hover:bg-[#F2CE2B]"
                    >
                        <MdAdd className="h-[18px] w-[18px]" /> Ajouter une offre
                    </button>
                </div>

                {/* Filters */}
                <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-[13px] border border-[#EFE0AE] bg-white px-3.5 py-3">
                    {[
                        {
                            label: 'Type',
                            value: selectedSubIndustry,
                            onChange: setSelectedSubIndustry,
                            options: [{ value: 'all', label: 'Toutes' }, ...subIndustries.map((s) => ({ value: s, label: s }))],
                        },
                        {
                            label: 'Prix',
                            value: selectedPriceRange,
                            onChange: setSelectedPriceRange,
                            options: priceRanges,
                        },
                        {
                            label: 'Leads',
                            value: selectedLeadRange,
                            onChange: setSelectedLeadRange,
                            options: leadRanges,
                        },
                    ].map(({ label, value, onChange, options }) => (
                        <label key={label} className="inline-flex items-center gap-2">
                            <span className="text-[13px] text-[#6E5A22]">{label}</span>
                            <select
                                className="rounded-[9px] border border-[#E6D6BC] bg-[#FFFDF7] px-3 py-2 text-[13.5px] font-semibold text-[#3B352C] outline-none transition-colors focus:border-[#D9A100]"
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                            >
                                {options.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </label>
                    ))}
                    <span className="flex-1" />
                    <span className="text-[13px] text-[#8A8172]">
                        {marketOffers.length} offres — {visibleOffers.length} affichées
                    </span>
                </div>

                {/* Cards grid */}
                {marketOffers.length === 0 ? (
                    <div className="rounded-[16px] border border-[#EFE0AE] bg-white px-4 py-12 text-center">
                        <p className="text-[14.5px] font-semibold text-[#3B352C]">Aucune offre disponible pour le moment</p>
                        <p className="mt-1 text-[13px] text-[#8A8172]">Les offres validées par notre équipe apparaîtront ici.</p>
                    </div>
                ) : visibleOffers.length === 0 ? (
                    <div className="rounded-[16px] border border-[#EFE0AE] bg-white px-4 py-12 text-center">
                        <p className="text-[14.5px] font-semibold text-[#3B352C]">Aucune offre ne correspond à ces filtres</p>
                        <p className="mt-1 text-[13px] text-[#8A8172]">Essayez de modifier vos critères de recherche.</p>
                    </div>
                ) : (
                    <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
                        {visibleOffers.map((offer) => (
                            <SellerMarketplaceCard
                                key={offer._id}
                                offerNumber={offer.offerNumber}
                                title={offer.subIndustry}
                                author={offer.industry}
                                price={displayRealPrice(offer.targetPrice, offer)}
                                image={getImage(offer.industry, offer.subIndustry)}
                                weeklyQuantity={offer.weeklyQuantity}
                                isGuest={isGuest}
                                onClick={() => nav(`/market-offer/${offer._id}`)}
                            />
                        ))}
                    </div>
                )}
            </div>

            <WelcomeSellerModal
                isOpen={showWelcomeModal}
                onClose={() => setShowWelcomeModal(false)}
                onSignUp={() => { setShowWelcomeModal(false); setShowSignUpModal(true); }}
            />
            <SignUpSellerModal
                isOpen={showSignUpModal}
                onClose={(success) => { setShowSignUpModal(false); if (success) initUser(); }}
            />
        </div>
    );
};

export default BuyerOffer;
